/**
 * A processor for the live score push messages that can
 * render score dom elements and produce animations.
 *
 * @module app/components/LiveScoreViewProcessor
 */
import _ from 'lodash';
import jQuery from 'jquery';
import Translator from './Translator';

/**
 * How long a goal animation last for
 *
 * @type {Integer}
 *
 * @private
 */
const animationDuration = 2000;

/**
 * Processing a live score message based on comment types,
 * used for the majority of sports.
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} scores data
 *
 * @returns {void}
 *
 * @private
 */
const genericLiveScoreProcessor = (eventContainer, scores) => {

    if (scores.type && scores.type !== 'GEN') {

        processLiveComment(eventContainer, scores);
    } else {

        showGenericScore(eventContainer, scores);
    }
};

/**
 * Processing a live score message for football. Updated the
 * minute of the game, the score and animates the number of
 * goals of a scoring (if we have a new goal), and also the
 * selection button briefly renders 'goal'.
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} scores data
 *
 * @returns {void}
 *
 * @private
 */
const footballLiveScoreProcessor = (eventContainer, scores) => {

    replaceLiveLabel(eventContainer, scores.set);
    const livescoreWrapper = eventContainer.find('.btmarket__livescore-wrapper');
    updateMatchScore(eventContainer, livescoreWrapper.last(), scores.leg, goalScoreAnimation);
};

/**
 * Processing a live score message for sports.
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} scores data
 *
 * @returns {void}
 *
 * @private
 */
const sharedLiveScoreProcessor = (eventContainer, scores) => {

    const livescoreWrapper = eventContainer.find('.btmarket__livescore-wrapper');
    updateMatchScore(eventContainer, livescoreWrapper.first(), scores.set, null);
    updateMatchScore(eventContainer, livescoreWrapper.last(), scores.leg, null);
};

/**
 * Processing a live score message for basketball.
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} scores data
 *
 * @returns {void}
 *
 * @private
 */
const basketballLiveScoreProcessor = (eventContainer, scores) => {

    replaceLiveLabel(eventContainer, scores.set);
    // replacePeriodLabel(eventContainer, scores.period);  // TODO: enable once templating is there
    const livescoreWrapper = eventContainer.find('.btmarket__livescore-wrapper');
    updateMatchScore(eventContainer, livescoreWrapper.last(), scores.leg, flashScoreAnimation);
};

/**
 * We split the 'leg' message text and populate 2 different elements that will allow
 * us to animate the individual score for a team or player in major sports like football
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} livescoreWrapper jQuery element representing the livescore section
 * @param {String} text The text used to generate the home and away score, that we can updated individually
 * @param {Function} anim  animation logic to run if score changes; set to null if no animation is wanted;
 *
 * @returns {void}
 *
 * @private
 */
const updateMatchScore = (eventContainer, livescoreWrapper, text, anim) => {

    const teamsScoresElements = livescoreWrapper.find('.btmarket__livescore-item');

    if (text) {

        updateTeamsScore(eventContainer, teamsScoresElements, text.split(' - '), anim);
    } else {

        teamsScoresElements.text('');
    }
};

/**
 * We split the 'leg' message text and populate 2 different elements that will allow
 * us to animate the individual score for a team or player in major sports like football
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} teamsScoresElements jQuery element holding team/players goals/points
 * @param {Array} teamScores A pair containing the goals/points for home and away team/player
 * @param {Function} anim  animation logic to run if score changes; set to null if no animation is wanted;
 *
 * @returns {void}
 *
 * @private
 */
const updateTeamsScore = (eventContainer, teamsScoresElements, teamScores, anim) => {

    if (teamScores.length === 2 && teamsScoresElements.length === 2) {

        updateTeamScore(
            eventContainer, teamScores[0],
            teamsScoresElements.first(), anim, 0
        );
        updateTeamScore(
            eventContainer, teamScores[1],
            teamsScoresElements.last(), anim, 1
        );
    } else {

        teamsScoresElements.text('');
    }
};

/**
 * Update a team score
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {String} newTeamScore The number of goals/points of the team/player as pushed in the livescore message
 * @param {Object} teamScoreElement The dom element that renders the goals/points of the team/player
 * @param {Function} anim animation logic to run if score changes; set to null if no animation is wanted;
 * @param {Number} position The position of the selection that scored: 0 for first, 1 for last
 *
 * @returns {void}
 *
 * @private
 */
const updateTeamScore = (eventContainer, newTeamScore, teamScoreElement, anim, position) => {

    const oldTeamScore = teamScoreElement.text().trim();

    if (oldTeamScore !== newTeamScore) {

        teamScoreElement.text(newTeamScore);

        // we use animations only if the old score was already set, ie not on first page render.
        if (anim !== null && oldTeamScore !== '') {
            anim(eventContainer, teamScoreElement, position);
        }
    }
};

/**
 * Flash the score digits that increased.
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} teamScoreElement The dom element that renders the goals/points of the team/player
 *
 * @returns {void}
 *
 * @private
 */
const flashScoreAnimation = (eventContainer, teamScoreElement) => {

    startGoalsAnimation(teamScoreElement,
        'btmarket__livescore-item--event wh-animated wh-animated--flash');
};

/**
 * Based on the sport of the event, specific containers must
 * be updated or animated. For example, in football, the price
 * for the scoring team should be replaced with the 'goal' text
 * in the button. Cricket or basketball do not show any score info,
 * just 'Live'. All other sports are using a generic processing,
 * based on the data from the live score message.
 *
 * @type {Object}
 *
 * @private
 */
const sportSpecificLiveScoreProcessors = {

    OB_SP9: footballLiveScoreProcessor,
    OB_SP24: sharedLiveScoreProcessor,
    OB_SP56: sharedLiveScoreProcessor,
    OB_SP27: basketballLiveScoreProcessor,
    OB_SP30: sharedLiveScoreProcessor,
};

/**
 * Choose the suitable processor for the event
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} scores data
 * @param {String} eventSportId  ID of the sport of the given event
 *
 * @returns {void}
 *
 * @private
 */
const processSportEventScores = (eventContainer, scores, eventSportId) => {

    if (sportSpecificLiveScoreProcessors.hasOwnProperty(eventSportId)) {

        eventContainer.find('.area-livescore').removeClass('wh-hide');
        sportSpecificLiveScoreProcessors[eventSportId](eventContainer, scores);
    } else {

        genericLiveScoreProcessor(eventContainer, scores);
    }
};

/**
 * For specific comment types (not generic), we render the
 * comment and replace live label with the set score.
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} scores data
 *
 * @returns {void}
 *
 * @private
 */
const processLiveComment = (eventContainer, scores) => {

    //ensure generic score is not displayed
    eventContainer.find('.btmarket__livescore-wrapper').addClass('wh-hide');

    //display comment and eventually score if present
    const comment = api.scoreTypeToText(scores.type);
    eventContainer.find('.btmarket__name > span:first').text(comment);

    if (scores.set) {

        replaceLiveLabel(eventContainer, scores.set);
    } else {

        replaceLiveLabel(eventContainer, 'Live');
    }
};

/**
 * Renders the periodName into period DOM element.
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} periodName name of current period to render.
 *
 * @returns {void}
 *
 * @private
 */
const replacePeriodLabel = (eventContainer, periodName) => {

    const periodElement = eventContainer.find('.btmarket__name--period').first();

    if (periodElement.text() !== periodName) {
        periodElement.text(periodName);
    }
};

/**
 * Processor for the live score push messages that can render
 * score dom elements and produce animations.
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} text to replace the default 'Live' label, might be current time or score depending on sport
 *
 * @returns {void}
 *
 * @private
 */
const replaceLiveLabel = (eventContainer, text) => {

    eventContainer.find('.wh-label')
        .first()
        .text(text);
};

/**
 * Show the score for generic sports
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} scores data
 *
 * @returns {void}
 *
 * @private
 */
const showGenericScore = (eventContainer, scores) => {

    //ensure comment is not displayed
    eventContainer.find('.btmarket__name > span:first').empty();
    replaceLiveLabel(eventContainer, 'Live');

    if (scores.set) {

        const livescoreWrapper = eventContainer.find('.btmarket__livescore-wrapper');
        livescoreWrapper.removeClass('wh-hide');
        updateMatchScore(eventContainer, livescoreWrapper.first(), scores.set, null);
    }
};

/**
 * Goal score animation
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Object} teamScoreElement The dom element that renders the goals/points of the team/player
 * @param {Number} position The position of the selection that scored: 0 for first, 1 for last
 *
 * @returns {void}
 *
 * @private
 */
const goalScoreAnimation = (eventContainer, teamScoreElement, position) => {

    flashScoreAnimation(eventContainer, teamScoreElement);
    animateEventMarkets(eventContainer, position);
};

/**
 * Start the goal animation.
 *
 * @returns {void}
 *
 * @private
 */
const startGoalsAnimation = (teamScoreElement, animationClass) => {

    teamScoreElement.addClass(animationClass);

    /**
     * Stops the goal number animation after the given duration
     *
     * @returns {void}
     *
     * @private
     */
    const stopGoalsNumberAnimation = () => teamScoreElement.removeClass(animationClass);

    window.setTimeout(stopGoalsNumberAnimation, animationDuration);
};

/**
 * Goal animation for 'win/draw/win' and 'win/win' football market sort codes
 *
 * @param {Object} eventContainer jQuery element representing the event subscribed to live scores
 * @param {Number} position The position of the selection that scored: 0 for first, 1 for last
 *
 * @returns {void}
 *
 * @private
 */
const animateEventMarkets = (eventContainer, position) => {

    /**
     * Checks if the given event market has a sort code that allows selection animation
     *
     * @param {Object} eventMarketDomElement jQuery market containing goal animated selections
     *
     * @returns {void}
     *
     * @private
     */
    const processEventMarket = (eventMarketDomElement) => {

        const marketModel = window.WH.sportsbook.model.markets[eventMarketDomElement.id];

        if (marketModel && _.includes(['MR', 'HH'], marketModel.sort)) {

            animateMarketSelection(eventMarketDomElement, position);
        }
    };

    const eventMarketDomElements = eventContainer.find('.btmarket__actions');
    _.each(eventMarketDomElements, processEventMarket);
};

/**
 * Identifies the selection that has to be animated according to the score change
 *
 * @param {Object} eventMarketDomElement jQuery market containing goal animated selections
 * @param {Number} position The position of the selection that scored: 0 for first, 1 for last
 *
 * @returns {void}
 *
 * @private
 */
const animateMarketSelection = (eventMarketDomElement, position) => {

    const marketSelectionDomElements = jQuery(eventMarketDomElement).find('.betbutton'),
        animationClass = 'betbutton--goal wh-animated wh-animated--flash';

    if (position) {

        startGoalsAnimation(marketSelectionDomElements.last(), animationClass);
    } else {

        startGoalsAnimation(marketSelectionDomElements.first(), animationClass);
    }
};

/**
 * Methods to be public
 *
 * @type {Object}
 */
const api = {

    /**
     * Returns the proper text for a commentary type
     *
     * @param {String} type Commentary type
     *
     * @returns {String} Message to be rendered
     *
     * @public
     */
    scoreTypeToText: (type) => {

        const key = 'components.liveScore.type.' + type;

        if (Translator.hasTranslation(key)) {

            return Translator.translate(key);
        }

        return Translator.translate('components.liveScore.type.LIVE');
    },

    /**
     * Processor for the live score push messages that can render
     * score dom elements and produce animations.
     *
     * @param {Object} eventContainer jQuery element
     * @param {Object} scores data
     *
     * @returns {void}
     *
     * @public
     */
    setEventScores: (eventContainer, scores) => {

        const eventModel = window.WH.sportsbook.model.events[scores.eventId];

        if (eventModel) {

            processSportEventScores(eventContainer, scores, eventModel.sportId);
        }
    },
};

export default api;
